/**
 * Code 128 (subset B) barcode encoder (§12.3) — dependency-free so it adds no
 * supply-chain surface (per the 14-day quarantine policy we vendor this instead
 * of pulling a barcode npm). Encodes the internal Order.id onto the packing slip;
 * the pack station reads it back to open the order at the bench (Tasks 1 & 6).
 *
 * Output is a list of bar runs (in module units) + the total module count, which
 * the <Barcode> component renders as crisp SVG <rect>s. Pure + DOM-free → unit
 * tested against a known Code-128B vector.
 */

// Canonical Code 128 element-width table, indexed by symbol value 0..106. Each
// entry is six module widths (bar,space,bar,space,bar,space) except the stop
// (106) which is seven (…it carries the terminating bar). This is the fixed,
// published Code 128 pattern set — do not edit.
const PATTERNS: readonly string[] = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312",
  "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222",
  "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131",
  "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321",
  "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121",
  "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224",
  "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114",
  "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112",
  "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113",
  "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412",
  "211214", "211232", "2331112",
];

const START_B = 104;
const STOP = 106;
const FIRST_PRINTABLE = 32; // Code-B value = charCode − 32 (covers ASCII 32..126)

/** Drop characters Code 128 subset B can't encode (keeps ids barcode-safe). */
function sanitize(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[^\x20-\x7e]/g, "");
}

/**
 * Encode a string to Code 128 subset B symbol values: start-B, one value per
 * character, the mod-103 checksum, then stop.
 */
export function code128Symbols(value: string): number[] {
  const text = sanitize(value);
  const codes: number[] = [START_B];
  let weightedSum = START_B; // checksum running total; start has positional weight 1
  text.split("").forEach((ch, i) => {
    const v = ch.charCodeAt(0) - FIRST_PRINTABLE;
    codes.push(v);
    weightedSum += v * (i + 1);
  });
  codes.push(weightedSum % 103); // checksum
  codes.push(STOP);
  return codes;
}

/** The concatenated module-width digit string for the whole symbol. */
export function code128Widths(value: string): string {
  return code128Symbols(value)
    .map((v) => PATTERNS[v] ?? "")
    .join("");
}

export interface BarcodeBars {
  /** Black bar runs in module units: each {x, width} measured in modules. */
  bars: Array<{ x: number; width: number }>;
  /** Total module count (without quiet zones). */
  modules: number;
}

/**
 * Resolve a value to drawable bar runs. The width string alternates bar,space
 * starting with a bar; we accumulate x and emit a run only for bars (odd-even
 * by position, position 0 = bar).
 */
export function code128Bars(value: string): BarcodeBars {
  const widths = code128Widths(value);
  const bars: Array<{ x: number; width: number }> = [];
  let x = 0;
  for (let i = 0; i < widths.length; i += 1) {
    const w = Number(widths[i]);
    if (i % 2 === 0) bars.push({ x, width: w }); // even index → bar
    x += w;
  }
  return { bars, modules: x };
}
